/**
 * LegalPage — single source of truth for all ASTIIV BASGAT legal text.
 *
 * One page renders 7 anchored sections (Terms, Privacy, Legal Agreement,
 * Compliance, Disclaimer, Data Usage, Partner Agreement). Routes hitting
 * /legal/<slug> auto-scroll to the relevant anchor. The content is written
 * to be legally read-able while complying with Indian IT Act 2000 + DPDP
 * Act 2023 conventions (and remaining usable for UAE customers under DIFC
 * guidance — ASTIIV BASGAT operates dual-region).
 */
import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, Shield, Gavel, AlertTriangle, Database, Handshake, Scale } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';

const SECTIONS = [
  { id: 'terms', icon: FileText, title: 'Terms & Conditions' },
  { id: 'privacy', icon: Shield, title: 'Privacy Policy' },
  { id: 'legal-agreement', icon: Gavel, title: 'Legal Agreement' },
  { id: 'compliance', icon: Scale, title: 'Compliance' },
  { id: 'disclaimer', icon: AlertTriangle, title: 'Disclaimer' },
  { id: 'data-usage', icon: Database, title: 'Data Usage' },
  { id: 'partner-agreement', icon: Handshake, title: 'Partner Agreement' },
];

const Section = ({ id, icon: Icon, title, children }) => (
  <section id={id} className="scroll-mt-28 mb-16" data-testid={`legal-section-${id}`}>
    <div className="flex items-center gap-3 mb-5">
      <Icon className="w-6 h-6 text-gold" />
      <h2 className="font-playfair text-2xl sm:text-3xl font-semibold">{title}</h2>
    </div>
    <div className="prose prose-invert max-w-none text-text-secondary text-[15px] leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

const LegalPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Map /legal/<slug> to the section anchor.
  useEffect(() => {
    const slug = location.pathname.replace(/^\/legal\/?/, '');
    if (!slug || slug === 'legal') return;
    const el = document.getElementById(slug);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      <header className="border-b border-white/10 sticky top-0 z-30 backdrop-blur-xl bg-obsidian/80">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-text-secondary hover:text-gold" data-testid="legal-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Logo to="/" size="sm" wordmarkClassName="text-gold" testId="legal-logo" />
          </div>
          <Link to="/" className="text-gold text-sm hover:underline" data-testid="legal-home-link">Home</Link>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-10 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-text-secondary mb-3">Sections</p>
            {SECTIONS.map(({ id, icon: Icon, title }) => (
              <a
                key={id}
                href={`#${id}`}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-text-secondary hover:text-gold hover:bg-white/5 transition-colors"
                data-testid={`legal-toc-${id}`}
              >
                <Icon className="w-4 h-4" />
                {title}
              </a>
            ))}
          </div>
        </aside>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-playfair text-4xl sm:text-5xl font-semibold mb-3">Legal</h1>
          <p className="text-text-secondary mb-12 max-w-2xl">
            ASTIIV BASGAT operates a dual-region platform (India & United Arab Emirates).
            The terms below govern your use of the service, our handling of your data,
            and the relationship between members, partners, and ASTIIV BASGAT.
          </p>

          <Section id="terms" icon={FileText} title="Terms & Conditions">
            <p>
              By creating an account or using ASTIIV BASGAT, you agree to these Terms &
              Conditions, the Privacy Policy, and any applicable region-specific
              addenda. You confirm you are at least 18 years old and legally
              competent to enter into a binding agreement under the laws of the
              jurisdiction you reside in.
            </p>
            <p>
              ASTIIV BASGAT provides a curated luxury real-estate and lifestyle
              marketplace, partner CRM, and AI-driven recommendation services.
              Listings published by third-party partners are the partners’
              responsibility — ASTIIV BASGAT does not warrant the accuracy or legal
              standing of any individual listing.
            </p>
            <p>
              You agree not to (a) scrape, mirror, or systematically copy content;
              (b) attempt to bypass region isolation or partner privacy controls;
              (c) misrepresent yourself when applying as a partner. Violation may
              result in immediate account suspension and recovery of damages.
            </p>
            <p>
              These terms are governed by the laws of India when accessed via the
              India portal and the laws of the United Arab Emirates (DIFC where
              applicable) when accessed via the UAE portal. Disputes are subject
              to the exclusive jurisdiction of the competent courts in Mumbai
              (India users) or DIFC Courts (UAE users).
            </p>
          </Section>

          <Section id="privacy" icon={Shield} title="Privacy Policy">
            <p>
              ASTIIV BASGAT collects only the personal information necessary to operate
              the platform: name, email, mobile number, region, dream profile
              preferences, and engagement signals (asset views, swipes, bookings).
              For Indian users, we comply with the Information Technology Act 2000
              and the Digital Personal Data Protection Act 2023.
            </p>
            <p>
              We never sell your personal data. Customer contact details are owned
              by ASTIIV BASGAT and are <em>not</em> shared with partners until you
              explicitly authorise a partner introduction or the ASTIIV BASGAT admin
              transfers the lead following our verification protocol (see Partner
              Agreement § 4).
            </p>
            <p>
              You may request a copy of your data, correction of inaccuracies, or
              deletion of your account at any time by writing to
              {' '}<a className="text-gold hover:underline" href="mailto:privacy@dreamlife.app">privacy@dreamlife.app</a>.
              Deletion requests are processed within 30 days, subject to
              statutory retention requirements (tax, audit, compliance).
            </p>
          </Section>

          <Section id="legal-agreement" icon={Gavel} title="Legal Agreement">
            <p>
              These terms, together with any partner-specific or premium-tier
              addendum you accept on signup, constitute the entire agreement
              between you and ASTIIV BASGAT and supersede all prior representations.
              If any provision is held unenforceable, the remainder remains in
              full force. Waiver of any breach is not a waiver of subsequent
              breaches.
            </p>
            <p>
              ASTIIV BASGAT may amend these terms with reasonable notice. Continued
              use after the effective date constitutes acceptance. Material
              changes that adversely affect existing paid subscribers will be
              communicated at least 30 days in advance.
            </p>
          </Section>

          <Section id="compliance" icon={Scale} title="Compliance">
            <p>
              <strong>India:</strong> Listings of under-construction residential
              projects require a valid RERA registration number where applicable
              under the Real Estate (Regulation and Development) Act, 2016.
              Partners are responsible for ensuring their RERA filing is current.
            </p>
            <p>
              <strong>UAE:</strong> Real-estate brokers and developers must be
              registered with the Dubai Land Department / RERA-Dubai and operate
              under a valid trade licence. ASTIIV BASGAT verifies the trade licence
              at onboarding and reserves the right to remove listings whose
              registrations have lapsed.
            </p>
            <p>
              All transactions on the platform are subject to applicable
              anti-money-laundering (AML) and know-your-customer (KYC) regulations.
              ASTIIV BASGAT may request additional verification for high-value
              transactions.
            </p>
          </Section>

          <Section id="disclaimer" icon={AlertTriangle} title="Disclaimer">
            <p>
              ASTIIV BASGAT’s AI-driven scores (ASTIIV BASGAT Score, Risk Score, Lead
              Score) are <strong>indicative analytical signals</strong> and do
              not constitute investment, financial, or legal advice. You should
              conduct your own due diligence — including on-site visits, title
              checks, and consultation with qualified professionals — before
              entering into any purchase or rental commitment.
            </p>
            <p>
              Indicative prices, projected yields, and completion dates are
              provided by partners and are subject to change without notice.
              Photographs, virtual tours, and floor plans are illustrative and
              may differ from the as-built reality.
            </p>
          </Section>

          <Section id="data-usage" icon={Database} title="Data Usage">
            <p>
              We use your data to (1) personalise asset bundles and search
              results, (2) match you with partners whose listings fit your
              declared preferences, (3) operate the payment and concierge
              experience, and (4) improve our recommendation models. Aggregated,
              anonymised analytics may be used for market reports.
            </p>
            <p>
              Lead scoring (Platinum / Gold / Silver / Nurture tiers) is
              computed from your profile completeness, declared budget, platform
              engagement, and membership tier — and is shared with the
              ASTIIV BASGAT-assigned partner in a PII-stripped form (first name
              only, no email, no phone) so partners can prioritise outreach
              without ever holding your contact details.
            </p>
            <p>
              You may opt out of behavioural personalisation from
              <span className="text-gold"> Account → Privacy</span>. Opting out
              degrades recommendation quality but does not affect access to
              the marketplace.
            </p>
          </Section>

          <Section id="partner-agreement" icon={Handshake} title="Partner Agreement">
            <p>
              <strong>1. Onboarding.</strong> Partners are admitted after a
              manual verification by ASTIIV BASGAT Admin (trade licence, RERA
              number, KYC). Approval grants access to the dashboard, asset
              creation tools, bulk upload, and the CRM.
            </p>
            <p>
              <strong>2. Listing Quality.</strong> Each asset must be accurate,
              non-misleading, and accompanied by adequate media (cover image,
              gallery, and where applicable a brochure and floor plan).
              ASTIIV BASGAT reserves the right to unpublish listings that do not
              meet quality standards.
            </p>
            <p>
              <strong>3. Region Lock.</strong> Partners operate strictly within
              the region they were approved for (India OR UAE — not both).
              Cross-region operation is treated as a material breach.
            </p>
            <p>
              <strong>4. Lead Privacy.</strong> Partners receive a redacted view
              of every lead — first name, lead score, lead tier, interest, and
              meeting time. Full customer contact details remain with ASTIIV BASGAT
              and are released only via the official “Transfer Lead” action
              executed by a ASTIIV BASGAT Admin after the customer authorises the
              introduction.
            </p>
            <p>
              <strong>5. Commercial Terms.</strong> Subscription fees,
              per-lead charges, and revenue-share terms are set out in the
              region-specific commercial schedule signed at onboarding.
              Disputes over invoiced amounts must be raised within 30 days.
            </p>
            <p>
              <strong>6. Termination.</strong> Either party may terminate the
              agreement with 30 days’ written notice. ASTIIV BASGAT may suspend
              access immediately for material breach (fraud, AML violations,
              repeated misrepresentation).
            </p>
          </Section>

          <div className="mt-16 pt-8 border-t border-white/10 text-text-secondary text-sm">
            <p>
              Last updated: 27 February 2026 · Version 1.4 · For questions email
              {' '}<a className="text-gold hover:underline" href="mailto:legal@dreamlife.app">legal@dreamlife.app</a>.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LegalPage;
